import { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { formatPhoneNumber } from "../../common/util";
import { STORAGE_BUCKET } from "../../common/constants";
import Images from "../../request/components/Images";
import moment from "moment";
import {
  Cancel as CancelIcon,
  CheckCircle,
  ExpandMoreOutlined,
  HourglassEmpty,
  RadioButtonUnchecked,
  OpenInNew,
} from "@mui/icons-material";
import StoreIcon from "@mui/icons-material/Store";

const activityLogColumns = [
  {
    field: "timestamp",
    headerName: "Timestamp",
    flex: 1,
    minWidth: 180,
    renderCell: (params) => {
      return moment(params.value).format("MMM D, YYYY h:mm:ss A");
    },
  },
  {
    field: "status",
    headerName: "Status",
    flex: 1,
    minWidth: 150,
  },
];

function providerDisplayName(name) {
  if (!name) return "";
  const n = String(name);
  return n.charAt(0).toUpperCase() + n.slice(1).toLowerCase();
}

function getDeliveryStatusVariant(status) {
  if (!status || typeof status !== "string") return { color: "default", icon: null };
  const s = status.toLowerCase();
  if (/completed|delivered|delivery_complete/.test(s)) {
    return { color: "success", icon: CheckCircle };
  }
  if (/cancelled|canceled|failed|error|rejected/.test(s)) {
    return { color: "error", icon: CancelIcon };
  }
  if (/pending|created|confirmed|accepted/.test(s)) {
    return { color: "default", icon: HourglassEmpty };
  }
  if (/pickup|in_transit|out_for_delivery|driver_at|on_the_way/.test(s)) {
    return { color: "info", icon: StoreIcon };
  }
  return { color: "default", icon: null };
}

function DeliveryStatus({ status }) {
  if (!status) return null;
  const { color, icon: Icon } = getDeliveryStatusVariant(status);
  return (
    <Chip
      size="small"
      label={String(status).toUpperCase()}
      color={color}
      icon={Icon ? <Icon /> : undefined}
    />
  );
}

function ActivityLogGrid({ delivery }) {
  if (!delivery?.log?.length) {
    return (
      <Typography variant="body2" color="textSecondary">
        No activity log entries.
      </Typography>
    );
  }
  return (
    <DataGrid
      rows={delivery.log.map((entry, index) => ({ id: index, ...entry }))}
      columns={activityLogColumns}
      autoHeight
      disableColumnMenu
      disableRowSelectionOnClick
      hideFooter={delivery.log.length <= 10}
      pageSizeOptions={[10, 25, 50]}
      initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
      sx={{ "& .MuiDataGrid-cell": { borderBottom: "1px solid #ECECEC" } }}
    />
  );
}

export default function ThirdPartyDelivery(props) {
  const { src, provider } = props;
  const [tab, setTab] = useState(0);

  const delivery = provider?.data;
  const providerName = provider ? providerDisplayName(provider.name) : "";
  const trackingLink = delivery?.dropoff?.trackingLink;

  const pickupComplete = !!delivery?.pickup?.time?.actual;
  const deliveryComplete = !!delivery?.dropoff?.time?.actual;
  const etaTime = pickupComplete ? delivery?.dropoff?.time?.eta : delivery?.pickup?.time?.eta;
  const etaMins = etaTime ? moment(etaTime).diff(moment(), "minutes") : null;
  const etaText = !deliveryComplete && +etaMins > 0 && +etaMins < 60 ? `${etaMins} mins away` : "";

  const tabConfigs = [
    { id: "details", label: "Details" },
    { id: "driver", label: "Driver" },
    { id: "activity", label: "Activity Log" },
    { id: "pod", label: "Proof of Delivery" },
  ];

  const currentTab = Math.min(tab, tabConfigs.length - 1);
  const activeTabId = tabConfigs[currentTab]?.id;

  useEffect(() => {
    setTab((t) => Math.min(t, tabConfigs.length - 1));
  }, [tabConfigs.length]);

  if (!provider) return null;

  return (
    <Accordion elevation={0} sx={{ border: "1px solid #DDD", "&:before": { display: "none" } }}>
      <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", pr: 1 }}>
          <Avatar
            sx={{ width: 40, height: 40 }}
            src={`${STORAGE_BUCKET}/img/logo/${provider.name.toLowerCase()}.webp`}
          />
          <Typography variant="h6" fontWeight={600}>
            {providerName}
          </Typography>
          • {delivery?.status && <DeliveryStatus status={delivery.status} />}
          {etaText && (
            <Typography variant="body2" color="textSecondary">
              • {etaText}
            </Typography>
          )}
          {src === "direct" && !provider.deliveryId && (
            <Chip label="Delivery Not Created" color="error" size="small" />
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        {delivery?.dsp?.delivery_id && (
          <Alert severity="info" sx={{ mb: 2 }}>
            For any issues with this delivery, please call DoorDash at (855) 222-8111 & give them
            the Delivery ID: <b>{delivery.dsp?.delivery_id}</b>
          </Alert>
        )}
        <Tabs value={currentTab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 40, mb: 2 }}>
          {tabConfigs.map((config) => (
            <Tab key={config.id} label={config.label} />
          ))}
        </Tabs>

        {activeTabId === "details" && (
          <Box>
            {delivery?.pickup && (
              <Box my={2}>
                <Stepper
                  orientation="vertical"
                  activeStep={
                    delivery.pickup?.time?.actual && delivery.dropoff?.time?.actual
                      ? 2
                      : delivery.pickup?.time?.actual
                        ? 1
                        : 0
                  }
                >
                  <Step completed={delivery.pickup?.status === "completed"}>
                    <StepLabel
                      icon={
                        delivery.pickup?.status === "completed" ? undefined : (
                          <RadioButtonUnchecked sx={{ fontSize: 26 }} color="primary" />
                        )
                      }
                      sx={{ textTransform: "uppercase" }}
                    >
                      Pickup
                      {(delivery.pickup.time?.actual ?? delivery.pickup.time?.eta) && (
                        <>
                          {" • "}
                          {moment(delivery.pickup.time.actual ?? delivery.pickup.time.eta).format(
                            "ddd MMM D, h:mm a"
                          )}
                        </>
                      )}
                    </StepLabel>
                    <StepContent TransitionProps={{ in: true }} sx={{ color: "#777" }}>
                      <Typography variant="body1" gutterBottom>
                        {delivery.pickup.name}
                      </Typography>
                      <Typography variant="body1">{delivery.pickup.location?.address}</Typography>
                    </StepContent>
                  </Step>
                  <Step completed={delivery.dropoff?.status === "completed"}>
                    <StepLabel
                      icon={
                        delivery.dropoff?.status === "completed" ? undefined : (
                          <RadioButtonUnchecked sx={{ fontSize: 26 }} color="primary" />
                        )
                      }
                      sx={{ textTransform: "uppercase" }}
                    >
                      Dropoff
                      {(delivery.dropoff?.time?.actual ?? delivery.dropoff?.time?.eta) && (
                        <>
                          {" • "}
                          {moment(delivery.dropoff.time.actual ?? delivery.dropoff.time.eta).format(
                            "ddd MMM D, h:mm a"
                          )}
                        </>
                      )}
                    </StepLabel>
                    <StepContent TransitionProps={{ in: true }} sx={{ color: "#777" }}>
                      <Typography variant="body1" gutterBottom>
                        {delivery.dropoff?.name}
                      </Typography>
                      {delivery.dropoff?.phone && (
                        <Typography variant="body1">
                          {formatPhoneNumber(delivery.dropoff.phone)}
                        </Typography>
                      )}
                      <Typography variant="body1">{delivery.dropoff?.location?.address}</Typography>
                      {trackingLink && (
                        <Box mt={1}>
                          <Button
                            startIcon={<OpenInNew />}
                            component="a"
                            target="_blank"
                            rel="noopener noreferrer"
                            href={trackingLink}
                            color="primary"
                          >
                            Live Tracking
                          </Button>
                        </Box>
                      )}
                    </StepContent>
                  </Step>
                </Stepper>
              </Box>
            )}
          </Box>
        )}

        {activeTabId === "driver" && (
          <Box>
            {delivery?.driver && (
              <Box mt={4}>
                <Typography variant="h6">{delivery.driver.name || "Not yet assigned"}</Typography>
                {delivery?.driver?.phone && (
                  <Typography variant="body1">
                    {formatPhoneNumber(delivery.driver.phone)}
                  </Typography>
                )}
                <Box mt={2}>
                  <Typography variant="body2" color="textSecondary">
                    Note: 3rd party drivers can only be reached by phone when delivery is
                    in-progress. The call only works if you call from your registered phone number,
                    which is usually your store phone number. Drivers are typically assigned shortly
                    before the scheduled pickup time.
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {activeTabId === "activity" && (
          <Box>
            <ActivityLogGrid delivery={delivery} />
          </Box>
        )}

        {activeTabId === "pod" && (
          <Box>
            {delivery?.pod?.photo?.length > 0 ? (
              <Images
                pics={delivery.pod.photo
                  .map((photo) =>
                    typeof photo === "string" ? photo : (photo?.url ?? photo?.src ?? "")
                  )
                  .filter(Boolean)}
              />
            ) : (
              <Typography variant="body2" color="textSecondary">
                No proof of delivery photos available.
              </Typography>
            )}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
